/**
 * Restrictive practice review clocks.
 *
 * The constants register finds no numeric statutory review interval
 * (Aged Care Rules 2025 rr 162-30(b), 162-35) — both clocks below are
 * TARGET, a Statura operational target, not a Standard citation.
 */
import type { ComplianceDeadlineItem } from './types';
import type { ComplianceDeadlineSettings } from '../compliance-settings';

const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;
const REVIEW_PROVISION =
  'Statura operational target (Aged Care Rules 2025 rr 162-30(b), 162-35 reference the obligation; no numeric interval found)';
const EMERGENCY_PROVISION =
  'Statura operational target (24h emergency review) — no verified numeric statutory clock';

export interface RestrictivePracticeReviewRow {
  id: string;
  reviewDate: string;
}

export type RestrictivePracticeReviewBand = 'DUE_SOON' | 'OVERDUE' | 'NOT_DUE';

export interface RestrictivePracticeReviewEvaluation {
  item: ComplianceDeadlineItem;
  band: RestrictivePracticeReviewBand;
  daysUntil: number;
}

/** Approaching within 14 days; overdue once the review date has passed. */
export function evaluateRestrictivePracticeReview(
  row: RestrictivePracticeReviewRow,
  now: Date,
  settings?: ComplianceDeadlineSettings,
): RestrictivePracticeReviewEvaluation {
  const warningLeadDays = settings?.restrictive_practice_review_warning_lead ?? 14;
  const reviewMs = new Date(row.reviewDate).getTime();
  const daysUntil = (reviewMs - now.getTime()) / MS_PER_DAY;
  const band: RestrictivePracticeReviewBand =
    daysUntil <= 0 ? 'OVERDUE' : daysUntil <= warningLeadDays ? 'DUE_SOON' : 'NOT_DUE';
  return {
    band,
    daysUntil,
    item: {
      id: `rp-review:${row.id}`,
      register: 'RESTRICTIVE_PRACTICE_REVIEW',
      recordRef: row.id,
      title: 'Restrictive practice review',
      dueAt: row.reviewDate,
      kind: 'TARGET',
      provisionOrTargetLabel: REVIEW_PROVISION,
      status: band === 'OVERDUE' ? 'OVERDUE' : band === 'DUE_SOON' ? 'DUE_SOON' : 'UPCOMING',
      href: `/compliance/restrictive-practices/${row.id}`,
      meta: { daysUntil },
    },
  };
}

export interface EmergencyRestrictivePracticeReviewRow {
  id: string;
  emergencyReviewDeadline: string;
}

export type EmergencyRestrictivePracticeReviewBand = 'DUE_SOON' | 'OVERDUE' | 'NOT_DUE';

export interface EmergencyRestrictivePracticeReviewEvaluation {
  item: ComplianceDeadlineItem;
  band: EmergencyRestrictivePracticeReviewBand;
  hoursUntil: number;
}

/** Approaching within 4h of the 24h emergency deadline; overdue once lapsed. */
export function evaluateEmergencyRestrictivePracticeReview(
  row: EmergencyRestrictivePracticeReviewRow,
  now: Date,
  settings?: ComplianceDeadlineSettings,
): EmergencyRestrictivePracticeReviewEvaluation {
  const reminderLeadHours = settings?.restrictive_practice_emergency_review_reminder_lead ?? 4;
  const deadlineMs = new Date(row.emergencyReviewDeadline).getTime();
  const hoursUntil = (deadlineMs - now.getTime()) / MS_PER_HOUR;
  const band: EmergencyRestrictivePracticeReviewBand =
    hoursUntil <= 0 ? 'OVERDUE' : hoursUntil <= reminderLeadHours ? 'DUE_SOON' : 'NOT_DUE';
  return {
    band,
    hoursUntil,
    item: {
      id: `rp-emergency-review:${row.id}`,
      register: 'RESTRICTIVE_PRACTICE_REVIEW',
      recordRef: row.id,
      title: 'Emergency restrictive practice review',
      dueAt: row.emergencyReviewDeadline,
      kind: 'TARGET',
      provisionOrTargetLabel: EMERGENCY_PROVISION,
      status: band === 'OVERDUE' ? 'OVERDUE' : band === 'DUE_SOON' ? 'DUE_SOON' : 'UPCOMING',
      href: `/compliance/restrictive-practices/${row.id}`,
      meta: { hoursUntil },
    },
  };
}
